import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Avatar,
    Divider,
    CardHeader
} from '@mui/material';
import {
    CheckCircle,
    Error,
    Business as BusinessIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';

import { usePostApiClients } from '@/api/gen/api';


interface RolePermission {
    role: string;
    permissionLevel: 'READ' | 'WRITE' | 'ADMIN';
}

interface CompanyData {
    clientName: string;
    clientPhoneContact: string;
    clientEmail: string;
    rolePermissions: RolePermission[];
}

interface Step1CompanyDataProps {
    onNext: (data: CompanyData, apiResponse: any) => void;
    initialData: Partial<CompanyData>;
}

// -------------------------- COMPONENTE PRINCIPAL --------------------------
const StepOneRegisterClient: React.FC<Step1CompanyDataProps> = ({
    onNext,
    initialData
}) => {
    // Estados 
    const [formData, setFormData] = useState<CompanyData>({
        clientName: initialData.clientName || '',
        clientPhoneContact: initialData.clientPhoneContact || '',
        clientEmail: initialData.clientEmail || '',
        rolePermissions: initialData.rolePermissions || [{ role: 'ADMIN', permissionLevel: 'ADMIN' }]
    });

    const [errors, setErrors] = useState<Partial<Record<keyof CompanyData, string>>>({});

    // Hooks
    const {
        mutate: createClient,
        isLoading,
        isSuccess,
        data: apiResponse
    } = usePostApiClients();

    // Manejadores 
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm() && !isLoading) {
            createClient({ data: formData });
        }
    };

    const handleContinueToStep2 = () => {
        if (apiResponse?.success) {
            onNext(formData, apiResponse);
        }
    };

    const handleInputChange = (field: keyof CompanyData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof CompanyData, string>> = {};

        if (!formData.clientName.trim()) {
            newErrors.clientName = 'El nombre del cliente es requerido';
        }

        if (!formData.clientEmail.trim()) {
            newErrors.clientEmail = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) {
            newErrors.clientEmail = 'El formato del email no es válido';
        }

        if (!formData.clientPhoneContact.trim()) {
            newErrors.clientPhoneContact = 'El teléfono de contacto es requerido';
        }

        // Validar roles
        const hasEmptyRoles = formData.rolePermissions.some(perm => !perm.role.trim());
        if (hasEmptyRoles) {
            newErrors.rolePermissions = 'Todos los roles deben tener un nombre';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // -------------------------- RENDERIZADO BASADO EN RESPUESTA REAL --------------------------

    // CASO 1: Éxito real (success: true)
    if (isSuccess && apiResponse?.success === true) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                    Cliente creado exitosamente
                </Alert>

                <Typography variant="h5" gutterBottom color="primary">
                    Paso 1 Completado: Cliente Creado
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main">
                            Cliente Creado - ID: {apiResponse.id}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Nombre:</Typography>
                                <Typography variant="body1">{formData.clientName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                <Typography variant="body1">{formData.clientEmail}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Teléfono:</Typography>
                                <Typography variant="body1">{formData.clientPhoneContact}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Estado:</Typography>
                                <Typography variant="body1" color="success.main">Creado Exitosamente</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleContinueToStep2}
                        startIcon={<CheckCircle />}
                    >
                        Continuar al Paso 2
                    </Button>
                </Box>
            </Box>
        );
    }

    // CASO 2: Éxito técnico pero fallo de negocio (success: false)
    if (isSuccess && apiResponse?.success === false) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="error" icon={<Error />} sx={{ mb: 3 }}>
                    ❌ Error al crear el cliente
                </Alert>

                <Typography variant="h5" gutterBottom color="error">
                    Paso 1 Fallido
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="error.main">
                            Error en la creación
                        </Typography>

                        {apiResponse.errors && apiResponse.errors.map((apiError: any, index: number) => (
                            <Alert
                                key={index}
                                severity="warning"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="subtitle2">
                                    {apiError.description}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Ubicación: {apiError.location}
                                </Typography>
                            </Alert>
                        ))}

                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                            <strong>Datos enviados:</strong>
                        </Typography>
                        <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                {JSON.stringify(formData, null, 2)}
                            </pre>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                    >
                        Volver a la creación de usuario
                    </Button>
                </Box>
            </Box>
        );
    }

    // -------------------------- RENDERIZADO DEL FORMULARIO --------------------------

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
                Los campos con * son obligatorios.
            </Alert>

            {/* Información Básica del Cliente */}
            <Card sx={{ mb: 1, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 1 }}>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: '#2196F3' }}>
                            <BusinessIcon />
                        </Avatar>
                    }
                    title="Información del Cliente"
                    subheader="Datos básicos de identificación del cliente"
                />
                <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Nombre del Cliente "
                            value={formData.clientName}
                            onChange={handleInputChange('clientName')}
                            error={!!errors.clientName}
                            helperText={errors.clientName || 'Nombre completo o razón social del cliente'}
                            disabled={isLoading}
                            required
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Información de Contacto */}
            <Card sx={{ mb: 1, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 1 }}>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: '#4CAF50' }}>
                            <PersonIcon />
                        </Avatar>
                    }
                    title="Información de Contacto"
                    subheader="Datos de contacto del cliente"
                />
                <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Correo Electrónico"
                            type="email"
                            value={formData.clientEmail}
                            onChange={handleInputChange('clientEmail')}
                            error={!!errors.clientEmail}
                            helperText={errors.clientEmail || 'Dirección de correo electrónico principal'}
                            disabled={isLoading}
                            required
                            InputProps={{
                                startAdornment: (
                                    <EmailIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Teléfono de Contacto "
                            value={formData.clientPhoneContact}
                            onChange={handleInputChange('clientPhoneContact')}
                            error={!!errors.clientPhoneContact}
                            helperText={errors.clientPhoneContact || 'Número de teléfono principal del cliente'}
                            disabled={isLoading}
                            required
                            InputProps={{
                                startAdornment: (
                                    <PhoneIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>


            {errors.rolePermissions && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.rolePermissions}
                </Alert>
            )}

            <Divider sx={{ my: 1 }} />
            {/* Botón de envío */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my:1 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? 'Creando Cliente...' : 'Crear Cliente y Continuar'}
                </Button>
            </Box>
        </Box>
    );
};

export default StepOneRegisterClient;